import { useEffect, useState } from "react";
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, AlertOctagon, Loader2, MinusCircle, ChevronDown, ChevronRight } from "lucide-react";
import { adminApi } from "../api";

const EUR = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

const STATUS_META = {
  ok:      { Icon: CheckCircle2,  color: "text-[#065F46]", bg: "bg-[#ECFDF5]", border: "border-[#A7F3D0]", label: "Activo (1h)" },
  idle:    { Icon: MinusCircle,   color: "text-[#92400E]", bg: "bg-[#FFFBEB]", border: "border-[#FCD34D]", label: "Inactivo (1h)" },
  stale:   { Icon: AlertTriangle, color: "text-[#9A3412]", bg: "bg-[#FFF7ED]", border: "border-[#FDBA74]", label: "Sem sinal (24h)" },
  silent:  { Icon: MinusCircle,   color: "text-[#6B7280]", bg: "bg-[#F9FAFB]", border: "border-[#E5E7EB]", label: "Sem registos" },
};

const OVERALL_META = {
  ok:      { color: "text-[#065F46]", bg: "bg-[#ECFDF5]", border: "border-[#A7F3D0]", label: "Webhooks saudáveis" },
  warning: { color: "text-[#92400E]", bg: "bg-[#FFFBEB]", border: "border-[#FCD34D]", label: "Webhooks lentos" },
  alert:   { color: "text-[#991B1B]", bg: "bg-[#FEF2F2]", border: "border-[#FCA5A5]", label: "Possível falha de webhook" },
};

function fmtAge(min) {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${Math.round(min % 60)}min`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/**
 * PaymentWebhooksHealth — saúde dos webhooks de pagamento.
 *
 * Útil durante a coexistência VM GCP + app nova: mostra para cada gateway
 * (EasyPay, EuPago, PayPal) quantas transições AP→PR ocorreram nas últimas
 * 1h e 24h e quanto tempo passou desde a última. Permite detectar em
 * tempo real se algum webhook está em baixo antes de cortar a VM legacy.
 */
export default function PaymentWebhooksHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await adminApi.paymentWebhooksHealth();
      setData(r);
    } catch (e) {
      setErr(e.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* refresh manual via botão; auto-poll opcional */ }, []);

  if (loading && !data) {
    return (
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md text-sm text-[#6B7280]" data-testid="webhooks-health-loading">
        <Loader2 size={14} className="inline mr-2 animate-spin" /> A verificar saúde dos webhooks…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-md text-sm text-[#991B1B]" data-testid="webhooks-health-error">
        Erro: {err}
      </div>
    );
  }
  if (!data) return null;

  const overall = OVERALL_META[data.overall_status] || OVERALL_META.ok;
  const stuckCount = (data.stuck_orders_in_ap || []).length;
  const ChevIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <section className="mb-8" data-testid="payment-webhooks-health">
      <div className={`border rounded-md ${overall.bg} ${overall.border}`}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="webhooks-health-toggle"
          aria-expanded={expanded}
          className="w-full text-left px-5 py-3 flex items-center justify-between gap-2 hover:opacity-90"
        >
          <div className="flex items-center gap-2 min-w-0">
            <ChevIcon size={16} className={`${overall.color} shrink-0`} />
            <Activity size={16} className={`${overall.color} shrink-0`} />
            <h2 className={`text-sm sm:text-base font-bold uppercase tracking-[0.12em] ${overall.color} leading-tight truncate`}>
              Saúde dos webhooks de pagamento · {overall.label}
            </h2>
            {stuckCount > 0 && (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#991B1B] bg-white border border-[#FCA5A5] rounded px-1.5 py-0.5"
                data-testid="webhooks-health-stuck-badge">
                {stuckCount} stuck
              </span>
            )}
          </div>
          <span className={`text-[10px] ${overall.color} opacity-70`}>{expanded ? "Encolher" : "Expandir"}</span>
        </button>

        {expanded && (
        <div className="px-5 pb-5">
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={load}
            data-testid="webhooks-health-refresh"
            className="text-xs text-[#0F1829] hover:text-[#0F1829] flex items-center gap-1 px-2 py-1 rounded border border-[#E6E1D6] bg-white shrink-0"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>

        <p className="text-xs text-[#4B5563] mb-3">
          Métricas baseadas em transições <strong>AP→PR</strong> nos últimos 30 dias. Durante a coexistência
          VM GCP / app nova, qualquer gateway sem transições há mais de 1h em horário comercial é candidato a investigação.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {(data.by_gateway || []).map((g) => {
            const meta = STATUS_META[g.status] || STATUS_META.silent;
            return (
              <div
                key={g.tipo}
                data-testid={`webhook-gateway-${g.tipo}`}
                className={`p-3 border rounded-md ${meta.border} ${meta.bg}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <meta.Icon size={14} className={meta.color} />
                    <span className="text-[11px] font-mono uppercase tracking-[0.12em] text-[#0F1829]">{g.tipo}</span>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.12em] font-bold ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="text-sm font-semibold text-[#0F1829]">{g.label}</div>
                <div className="grid grid-cols-3 gap-1 mt-2 text-[10px]">
                  <div>
                    <div className="text-[#978D78] uppercase tracking-[0.1em]">1h</div>
                    <div className="text-sm font-mono text-[#0F1829]">{g.count_1h}</div>
                  </div>
                  <div>
                    <div className="text-[#978D78] uppercase tracking-[0.1em]">24h</div>
                    <div className="text-sm font-mono text-[#0F1829]">{g.count_24h}</div>
                  </div>
                  <div>
                    <div className="text-[#978D78] uppercase tracking-[0.1em]">30d</div>
                    <div className="text-sm font-mono text-[#0F1829]">{g.count_30d}</div>
                  </div>
                </div>
                <div className="text-[10px] text-[#6B7280] mt-1.5">
                  Última: <span className="font-mono">{fmtAge(g.minutes_since_last)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {stuckCount > 0 && (
          <div className="mt-4 p-3 bg-white border border-[#FCA5A5] rounded-md" data-testid="webhooks-stuck-orders">
            <div className="flex items-center gap-2 mb-2">
              <AlertOctagon size={14} className="text-[#991B1B]" />
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#991B1B]">
                {stuckCount} encomenda{stuckCount === 1 ? "" : "s"} em AP há &gt; 30min com pagamento online
              </span>
            </div>
            <ul className="divide-y divide-[#FED7AA] text-xs">
              {data.stuck_orders_in_ap.slice(0, 8).map((o) => (
                <li key={o.ID_Encomenda} className="py-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`webhooks-stuck-row-${o.ID_Encomenda}`}>
                  <span className="font-mono text-[#0F1829]">#{o.ID_Encomenda}</span>
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#978D78]">{o.ID_TipoPagamento}</span>
                  <span className="text-[#4B5563] font-mono">{EUR(o.PrecoFinal)}</span>
                  <span className="text-[#6B7280] truncate flex-1 min-w-0 basis-full sm:basis-auto">{o.NomeCompleto}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
        )}
      </div>
    </section>
  );
}
